import React, {useState, useEffect} from "react";
import Title from "../dashboard/Title";
import Button from "@material-ui/core/Button";
import UserService from "../../services/user.service";
import {logOut} from "../../helpers/logout";
import {NotificationManager} from 'react-notifications';
import Paper from "@material-ui/core/Paper";
import TextInput from "../form/input/TextInput";
import Popup from "./Popup";
import ActionButton from "../form/input/ActionButton";
import Tooltip from "@material-ui/core/Tooltip";
import {InputAdornment} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import PhoneIcon from "@material-ui/icons/Phone";
import {Search} from "@material-ui/icons";
import ConfirmDialog from "./ConfirmDialog";
import useTable from "./useTable";
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import {useDashboardStyles, BRAND} from "../settings/dashboardStyles";
import {makeStyles} from "@material-ui/core/styles";

const useLocalStyles = makeStyles((theme) => ({
    publicBadgeYes: {
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 9px",
        background: BRAND.successBg,
        color: BRAND.success,
        borderRadius: 999,
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        border: `1px solid ${BRAND.successBorder}`,
    },
    publicBadgeNo: {
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 9px",
        background: "rgba(90, 102, 117, 0.10)",
        color: BRAND.inkSoft,
        borderRadius: 999,
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        border: "1px solid rgba(90, 102, 117, 0.22)",
    },
    loadMoreRow: {
        marginTop: 12,
        display: "flex",
        justifyContent: "center",
    },
    contactToolbar: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        width: "100%",
        "& button": {
            flexShrink: 0,
        },
        [theme.breakpoints.down("xs")]: {
            flexDirection: "column",
            alignItems: "stretch",
        },
    },
    searchInput: {
        flex: "1 1 auto",
        minWidth: 260,
        [theme.breakpoints.down("xs")]: {
            minWidth: 0,
        },
    },
    actionsCell: {
        whiteSpace: "nowrap",
        width: 1,
    },
    actionsRow: {
        display: "inline-flex",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 6,
    },
}));

export default function ContactList(props) {
    const {publicMode = false} = props;
    const classes = useDashboardStyles();
    const localClasses = useLocalStyles();

    const [contacts, setContacts] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [filterFn, setFilterFn] = useState({
        fn: items => items
    });
    const [limit, setLimit] = useState(100);

    const [values, setValues] = useState({
        _id: null,
        name: '',
        status: 1,
        defaultContact: ''
    });

    const headCells = [
        {id: 'name', label: 'Názov'},
        {id: 'phone', label: 'Telefón'},
        {id: 'email', label: 'Email'},
        {id: 'public', label: 'Verejné'},
        {id: 'actions', label: 'Akcie', disableSorting: true, align: 'right'}
    ]

    const {
        TblContainer,
        TblHead,
        TblPagination,
        recordsAfterPagingAndSorting
    } = useTable(contacts, headCells, filterFn);

    const [show, setShow] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({isOpen: false, title: '', subTitle: ''})

    const handleChange = (event) => {
        const {name, value} = event.target;
        setValues({...values, [name]: value});
    }

    useEffect(() => {
        const search = searchText.trim().toLowerCase();
        setFilterFn({
            fn: items => {
                if (!search) return items;

                return items.filter(item =>
                    (item.name || '').toLowerCase().includes(search) ||
                    (item.email || '').toLowerCase().includes(search) ||
                    (item.phone || '').toLowerCase().includes(search)
                );
            }
        });
    }, [searchText]);

    const hasPhone = (item) => Boolean((item.phone || '').trim());

    const handleCall = (phone) => () => {
        if (!phone) return;
        window.location.href = `tel:${phone}`;
    };

    const loadContacts = () => {
        const request = publicMode ? UserService.getPublicContacts(limit) : UserService.getContacts(limit);
        request.then(
            (response) => {
                setContacts(response.data);
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                }
            }
        );
    };

    useEffect(() => {
        loadContacts();
    }, [limit, publicMode]);

    const resetToDefault = () => {
        setValues({
            _id: null,
            name: '',
            status: 0,
        });
        setShow(false);
    }

    const errNotification = (e) => {
        if (e.response.status === 401) {
            logOut(props);
            NotificationManager.error('Musíte sa nanovo prihlásiť.', 'Chyba', 5000);
        } else {
            NotificationManager.error('Došlo k neočakávanej chybe. Skúste neskôr alebo kontaktujte podporu.', 'Chyba', 5000);
        }
    }

    const handleCreate = (e) => {
        e.preventDefault();

        if (validate()) {
            let data = {
                _id: values._id,
                name: values.name,
                phone: values.phone,
                email: values.email,
                public: publicMode
            };

            if (values._id) {
                UserService.updateContact(data).then(
                    () => {
                        loadContacts();
                        NotificationManager.success('Kontakt upravený!', 'Upravené', 5000);
                        resetToDefault();
                    },
                    (e) => {
                        errNotification(e);
                    }
                );
            } else {
                data.created = new Date();
                UserService.addContact(data).then(
                    () => {
                        loadContacts();
                        NotificationManager.success('Kontakt vytvorený!', 'Vytvorené', 5000);
                        resetToDefault();
                    },
                    (e) => {
                        errNotification(e);
                    }
                );
            }
        } else {
            NotificationManager.error('Skontrolujte vyplnený formulár!', 'Chyba', 5000);
        }
    };

    const [errors, setErrors] = useState({});

    const validate = () => {
        const email = (values.email || '').trim();
        let temp = {};
        temp.name = values.name !== '' ? "" : "Vyplne názov."
        temp.email = !email || /^\S+@\S+\.\S+$/.test(email) ? "" : "Zadajte platnú emailovú adresu."
        setErrors({...temp})
        // eslint-disable-next-line eqeqeq
        return Object.values(temp).every(x => x == "")
    }

    const handleDelete = (id) => {
        setConfirmDialog({
            ...confirmDialog,
            isOpen: false
        })
        UserService.deleteContact(id).then(
            () => {
                NotificationManager.success('Kontakt vymazaný!', 'Vymazané', 5000);
                loadContacts();
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                    NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                }
                NotificationManager.error('Kontakt sa nepodarilo odstrániť!', 'Chyba', 5000);
            }
        );
    }

    const handleEdit = (vals) => () => {
        setValues(vals);
        setShow(true);
    }

    const handleNoLimit = () => {
        setLimit(0);
    }

    return (
        <Paper className={classes.paper} elevation={0}>
            <div className={classes.sectionHeader}>
                <Title>{publicMode ? 'Zoznam verejných kontaktov' : 'Zoznam mojich kontaktov'}</Title>
                <div className={`${classes.sectionToolbar} ${localClasses.contactToolbar}`}>
                    <TextInput
                        className={localClasses.searchInput}
                        label="Vyhľadať kontakt"
                        placeholder="Meno, email alebo telefón"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small"/>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        startIcon={<AddIcon/>}
                        className={classes.primaryAction}
                        onClick={() => setShow(true)}
                    >
                        {publicMode ? 'Pridať verejný kontakt' : 'Pridať kontakt'}
                    </Button>
                </div>
            </div>

            {contacts.length === 0 ? (
                <div className={classes.emptyState}>
                    {publicMode ? 'Zatiaľ nie sú pridané žiadne verejné kontakty.' : 'Zatiaľ nemáte pridané žiadne kontakty.'}
                </div>
            ) : (
                <>
                    <TblContainer>
                        <TblHead/>
                        <TableBody>
                            {recordsAfterPagingAndSorting().map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell><strong>{item.name}</strong></TableCell>
                                    <TableCell>{item.phone}</TableCell>
                                    <TableCell>{item.email}</TableCell>
                                    <TableCell>
                                        <span className={item.public ? localClasses.publicBadgeYes : localClasses.publicBadgeNo}>
                                            {item.public ? 'Áno' : 'Nie'}
                                        </span>
                                    </TableCell>
                                    <TableCell align="right" className={localClasses.actionsCell}>
                                        <div className={localClasses.actionsRow}>
                                            <Tooltip title={hasPhone(item) ? "Zavolať" : "Telefónne číslo nie je vyplnené"}>
                                                <span>
                                                    <ActionButton color="primary" disabled={!hasPhone(item)} onClick={handleCall(item.phone)}>
                                                        <PhoneIcon fontSize="small"/>
                                                    </ActionButton>
                                                </span>
                                            </Tooltip>
                                            <ActionButton color="primary">
                                                <Tooltip title="Upraviť kontakt">
                                                    <EditIcon onClick={handleEdit(item)} fontSize="small"/>
                                                </Tooltip>
                                            </ActionButton>
                                            <ActionButton color="secondary">
                                                <Tooltip title="Vymazať kontakt">
                                                    <DeleteIcon
                                                        fontSize="small"
                                                        onClick={() => {
                                                            setConfirmDialog({
                                                                isOpen: true,
                                                                title: 'Naozaj chcete vymazať tento kontakt?',
                                                                subTitle: "Túto operáciu nevieš vrátiť.",
                                                                onConfirm: () => handleDelete(item._id)
                                                            })
                                                        }}
                                                    />
                                                </Tooltip>
                                            </ActionButton>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </TblContainer>
                    <TblPagination/>
                    <div className={localClasses.loadMoreRow}>
                        <Button size="small" className={classes.secondaryAction} onClick={handleNoLimit}>
                            Načítať viac ako max. 100 záznamov
                        </Button>
                    </div>
                </>
            )}

            <Popup
                title={values._id ? 'Úprava kontaktu' : 'Pridanie kontaktu'}
                openPopup={show}
                setOpenPopup={setShow}
                additionalWork={resetToDefault}
                maxW="sm"
                actions={
                    <Button
                        variant="contained"
                        type="submit"
                        form="contact-form"
                        className={classes.primaryAction}
                    >
                        {values._id ? "Upraviť kontakt" : (publicMode ? "Pridať verejný kontakt" : "Pridať kontakt")}
                    </Button>
                }
            >
                <form id="contact-form" autoComplete='off' onSubmit={handleCreate}>
                    <div className={classes.formField}>
                        <TextInput label="Názov" error={errors.name} name={'name'} value={values.name || ''} onChange={handleChange} fullWidth/>
                    </div>
                    <div className={classes.formField}>
                        <TextInput label="Telefón" error={errors.phone} name={'phone'} value={values.phone || ''} onChange={handleChange} fullWidth/>
                    </div>
                    <div className={classes.formField}>
                        <TextInput label="Email" error={errors.email} name={'email'} value={values.email || ''} onChange={handleChange} fullWidth/>
                    </div>
                </form>
            </Popup>

            <ConfirmDialog
                confirmDialog={confirmDialog}
                setConfirmDialog={setConfirmDialog}
                icon={<DeleteIcon/>}
            />
        </Paper>
    );
}
